import { ScreenerSettingName } from '@/hooks/screener-settings';
import { isProductTourEnabledForFacility } from '@/lib/productTourGating';
import { buildProductTourFacilityMetadata } from '@/lib/productTourMetadata';
import { UserModel } from '@/models/UserModel';

import { Loadable } from './Loadable';

export class Pendo extends Loadable {
  public static identify(user: UserModel): void {
    if (!user.facility_id || !Pendo.isEnabled(user.facility_id)) {
      return;
    }

    Pendo.ensureScriptLoaded();
    window.pendo?.initialize(Pendo.buildInitializeOptions(user));
  }

  public load(): void {
    Pendo.ensureScriptLoaded();
  }

  private static ensureScriptLoaded(): void {
    const apiKey = import.meta.env.VITE_PENDO_API_KEY;
    if (!apiKey) {
      console.warn('Pendo API key not configured. Skipping initialization.');
      return;
    }

    if (
      document.querySelector(
        `script[src="https://cdn.pendo.io/agent/static/${apiKey}/pendo.js"]`
      )
    ) {
      return;
    }

    (function (pendoApiKey: string) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (function (p: any, e: Document, n: 'script', d: string, o?: any) {
        let v: string[], w: number, x: number;
        o = p[d] = p[d] || {};
        o._q = o._q || [];
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
        for (w = 0, x = v.length; w < x; ++w) {
          (function (m: string) {
            o[m] =
              o[m] ||
              function () {
                o._q[m === v[0] ? 'unshift' : 'push'](
                  [m].concat([].slice.call(arguments, 0))
                );
              };
          })(v[w]!);
        }
        const y: HTMLScriptElement = e.createElement(n);
        y.async = true;
        y.src =
          'https://cdn.pendo.io/agent/static/' + pendoApiKey + '/pendo.js';
        const z = e.getElementsByTagName(n)[0];
        if (z?.parentNode) {
          z.parentNode.insertBefore(y, z);
        } else {
          e.head.appendChild(y);
        }
      })(window, document, 'script', 'pendo');
    })(apiKey);
  }

  private static buildInitializeOptions(user: UserModel) {
    return {
      visitor: {
        id: user.id,
        email: user.email,
        full_name: user.fullName,
        user_role: user.user_type?.title,
        user_title: user.title,
        ...buildProductTourFacilityMetadata(user)
      },
      account: user.organization_details
        ? {
            id: user.organization_details.id,
            name: user.organization_details.name
          }
        : undefined
    };
  }

  private static isEnabled(facilityId: string): boolean {
    if (!import.meta.env.VITE_PENDO_API_KEY) {
      return false;
    }

    return isProductTourEnabledForFacility(
      ScreenerSettingName.PendoProductTours,
      facilityId
    );
  }
}
